import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createPortal } from "react-dom";
import { useNavigate } from 'react-router-dom';
import LanguageSelector from './LanguageSelector'; // Adjust path as needed
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import { StatusBadgeSm } from '../../utils/StatusBadge';
import GetCookie from '../../utils/GetCookie';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import TrimContent from '../../utils/TrimContent';
import postNdjson from '../../utils/postNdjson';
import authNavigate from '../../globalComponents/AuthNav';
import { PERMISSIONS, capabilityChecker } from '../../utils/permissionRoutes';

const abbrSelectionStore = new Map();
const AUTOGEN_TYPE_LABELS = {
  LIST_OF_FIGURES: "List of Figures",
  LIST_OF_TABLES: "List of Tables",
  ABBREVIATIONS: "Abbreviations",
};
const _normShort = (s) => (s || "").trim().toUpperCase();
const _abbrKey = (a) =>
  `${_normShort(a.short_form)}|${(a.full_form || "").trim().toUpperCase()}`;

// ── Mismatch review helpers ───────────────────────────────────────────────────
// A candidate has no id of its own; this is what identifies it in the panel.
const _candKey = (c) => `${c.kind}|${c.matches_short_form}|${(c.found || "").toLowerCase()}`;

// Words and separators kept as separate pieces, so a comparison never lands in the
// middle of one. Splitting "Risikomanagement-Plans" against "Risk-Management-Plan" by
// character marks "Ris|ikomanagement-Plans", which reads as nonsense; by word it marks
// the two whole words that really differ.
const _tokenise = (text) => (text || "").match(/[^\s\-–—/(),.;:]+|[\s\-–—/(),.;:]+/g) || [];

// Trim the words the two spellings share at each end, leaving only the part that
// differs. Two near-identical strings side by side are not comparable at a glance.
const _diffSplit = (text = "", other = "") => {
  const mine = _tokenise(text);
  const theirs = _tokenise(other);
  let head = 0;
  while (head < mine.length && head < theirs.length && mine[head] === theirs[head]) head += 1;
  let tail = 0;
  while (
    tail < mine.length - head &&
    tail < theirs.length - head &&
    mine[mine.length - 1 - tail] === theirs[theirs.length - 1 - tail]
  ) tail += 1;
  return {
    head: mine.slice(0, head).join(""),
    mid: mine.slice(head, mine.length - tail).join(""),
    tail: mine.slice(mine.length - tail).join(""),
  };
};

const _diffHint = (text = "", other = "") => {
  if (text === other) return "";
  if (text.toLowerCase() === other.toLowerCase()) return "capitalisation only";
  const strip = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (strip(text) === strip(other)) return "spacing or punctuation only";
  return "";
};

const ReportModalForm = ({ isOpen, onClose, templateDetails, updFunc, formData, capabilities = {}, abbreviationsOnly = false, hasPendingChanges }) => {
  const canHere = capabilityChecker(capabilities);
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [languages, setLanguages] = useState({});
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState('DOCX');
  const [isGenerating, setIsGenerating] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const badgeRef = useRef(null);
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Abbreviation States
  const [showAbbrevModal, setShowAbbrevModal] = useState(false);
  const [abbreviations, setAbbreviations] = useState([]);
  const [abbrevSearch, setAbbrevSearch] = useState('');
  const [selectedAbbrevIds, setSelectedAbbrevIds] = useState([]);
  const [isAddingAbbrev, setIsAddingAbbrev] = useState(false);
  const [newShortForm, setNewShortForm] = useState('');
  const [newFullForm, setNewFullForm] = useState('');
  const [newIsPrimary, setNewIsPrimary] = useState(false);
  const [newOriginalFullForm, setNewOriginalFullForm] = useState('');
  const [newOriginalLanguage, setNewOriginalLanguage] = useState('');
  const [newIsForeignOrigin, setNewIsForeignOrigin] = useState(false);
  const [editingAbbrevId, setEditingAbbrevId] = useState(null);
  const [editShortForm, setEditShortForm] = useState('');
  const [editFullForm, setEditFullForm] = useState('');
  const [editOriginalFullForm, setEditOriginalFullForm] = useState('');
  const [editOriginalLanguage, setEditOriginalLanguage] = useState('');
  const [editIsForeignOrigin, setEditIsForeignOrigin] = useState(false);
  const [isSavingAbbrev, setIsSavingAbbrev] = useState(false);
  const [showDeleteAbbrevConfirm, setShowDeleteAbbrevConfirm] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState([]);

  // Mismatch Review States
  const [showMismatchPanel, setShowMismatchPanel] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [mismatchCandidates, setMismatchCandidates] = useState([]);
  const [mismatchNotice, setMismatchNotice] = useState('');
  // candidate key -> 'registered' | 'found'. Which spelling the report should print.
  const [primaryChoice, setPrimaryChoice] = useState({});
  // Ticked for the one bulk save. Rejected rows stay on screen, greyed, so a reviewer
  // can change their mind. Excerpts are collapsed until asked for.
  const [pickedKeys, setPickedKeys] = useState(() => new Set());
  const [rejectedKeys, setRejectedKeys] = useState(() => new Set());
  const [expandedExcerpts, setExpandedExcerpts] = useState(() => new Set());
  const [isAcceptingPicked, setIsAcceptingPicked] = useState(false);
  // Ids inserted during this popup session, for the "New" badge. Session-scoped on
  // purpose: it answers "what did I just add", not "what is recent".
  const [newAbbrevIds, setNewAbbrevIds] = useState(() => new Set());
  // The is_primary flags as the server last returned them, so the footer Save knows
  // which rows the user actually flipped.
  const [serverPrimaryIds, setServerPrimaryIds] = useState([]);
  const [editPrimarySnapshot, setEditPrimarySnapshot] = useState(null);

  // Front Page States
  const [frontPageFile, setFrontPageFile] = useState(null);
  const [prevTemplateId, setPrevTemplateId] = useState(null);
  const [primaryAbbrevIds, setPrimaryAbbrevIds] = useState([]);

  // Agent Selection States
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [agentSearch, setAgentSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedSectionKeys, setSelectedSectionKeys] = useState(new Set());
  const [showDependencyWarning, setShowDependencyWarning] = useState(false);
  const [missingDependencies, setMissingDependencies] = useState([]);
  const [selectedDepIds, setSelectedDepIds] = useState(new Set());

  const getSelMap = () => {
    const t = templateDetails?.id;
    if (!abbrSelectionStore.has(t)) abbrSelectionStore.set(t, new Map());
    return abbrSelectionStore.get(t);
  };
  // Fetch languages on modal open
  const API_BASE_URL = '/pceapi/genbi';

  const headers = {
    'Content-Type': 'application/json',
    Authorization: GetCookie('access_token')
  };
  useEffect(() => {
    if (isOpen) {
      fetchLanguages();
      fetchAbbreviations();
      if (templateDetails?.id !== prevTemplateId) {
        setSelectedAbbrevIds([]);
        setFrontPageFile(null);
        setSelectedLanguage(null);
        setSelectedFileType("");
        setPrimaryAbbrevIds([]);
        setPrevTemplateId(templateDetails?.id);
      }
      setSelectedFileType('DOCX');

      // Init agent selector
      if (formData?.sectionOrder?.length > 0) {
        const initSelectedIds = new Set();
        formData.sectionOrder.forEach(secKey => {
          const sec = formData.sectionsDict[secKey];
          if (!sec) return;
          sec.agentOrder.forEach(agKey => {
            const agent = sec.agentsDict[agKey];
            if (agent?.id) initSelectedIds.add(agent.id);
          });
        });
        setSelectedAgentIds(initSelectedIds);
        setSelectedSectionKeys(new Set(formData.sectionOrder.map(k => formData.sectionsDict[k]?.section_ordering_id).filter(Boolean)));
      } else {
        setSelectedAgentIds(new Set());
        setSelectedSectionKeys(new Set());
      }
      setAgentSearch('');
      setExpandedSections({});
    }

    if (isOpen && abbreviationsOnly) {
      setShowAbbrevModal(true);
    }

    // Key on the id, NOT the templateDetails object. Both call sites in
    // GenbiTemplateTabViewer build it inline as {id, name}, so it is a new object on
    // every parent render — and with the object in here, a window resize re-ran this
    // whole effect: two fetches, plus a reset of the agent selection, the search box
    // and the expanded sections. The effect only ever reads the id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, templateDetails?.id]);


  const fetchAbbreviations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get-abbreviations?template_id=${templateDetails.id}`, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (data.status === 'success') {

        const rawData = data.data?.abbreviations || data.abbreviations || {};

        let fetchedList = [];

        if (Array.isArray(rawData)) {

          fetchedList = rawData;
        } else if (typeof rawData === 'object' && rawData !== null) {

          fetchedList = Object.entries(rawData).map(([key, value]) => ({
            id: parseInt(key),
            ...value
          }));
        }
        fetchedList.sort((a, b) => {

          const nameA = a.full_form?.toLowerCase() || "";
          const nameB = b.full_form?.toLowerCase() || "";
          return nameA.localeCompare(nameB);
        });

        setAbbreviations(fetchedList);
        const primaryIds = fetchedList
          .filter((abbr) => abbr.is_primary)
          .map((abbr) => abbr.id);
        setPrimaryAbbrevIds(primaryIds);
        setServerPrimaryIds(primaryIds);
        const selMap = getSelMap();
        let selectedList;
        if (selMap.size > 0) {
          // Session cache exists — hybrid match (exact key + short_form fallback)
          const freshExact = new Set(fetchedList.map((a) => _abbrKey(a)));
          const fallbackShorts = new Set(
            [...selMap.entries()]
              .filter(([k]) => !freshExact.has(k))
              .map(([, short]) => short)
          );
          selectedList = fetchedList.filter(
            (a) => selMap.has(_abbrKey(a)) || fallbackShorts.has(_normShort(a.short_form))
          );
        } else {
          // No session cache — restore from DB is_selected
          selectedList = fetchedList.filter((a) => a.is_selected);
        }
        // Sync module store with current selection
        const healed = new Map(selectedList.map((a) => [_abbrKey(a), _normShort(a.short_form)]));
        abbrSelectionStore.set(templateDetails?.id, healed);
        setSelectedAbbrevIds(selectedList.map((a) => a.id));
      } else {

        console.error("Failed to load abbreviations", data);
        setAbbreviations([]);
      }
    } catch (error) {
      console.error('Error fetching abbreviations:', error);
      setAbbreviations([]);
    } finally {
    }
  };

  const fetchLanguages = async () => {
    setIsLoadingLanguages(true);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/langs`, {
        method: 'GET',
        headers: headers
      });

      const data = await response.json();

      if (data.status === 'success') {
        setLanguages(data.langs || {});
      } else {
        setErrorMessage(data.message || 'Failed to fetch languages');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setErrorMessage('Network error occurred while fetching languages');
      setIsErrorPopupVisible(true);
    } finally {
      setIsLoadingLanguages(false);
      setIsLoading(false);
    }
  };
  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
  };

  const handleFileTypeSelect = (fileType) => {
    setSelectedFileType(fileType);
  };

  const handleGenerate = async (extraAgentIds = null) => {
    if (!selectedFileType || !templateDetails) return;

    setIsGenerating(true);

    // Saving pending changes can throw (e.g. a preset with no sections). Bail out with its
    // own message, and clear the generating state so Cancel stays usable.
    try {
      await updFunc();
    } catch (error) {
      console.error('Error saving preset before generating:', error);
      setErrorMessage(error.message || 'Failed to save preset changes');
      setIsErrorPopupVisible(true);
      setIsGenerating(false);
      return;
    }

    try {
      // CHANGE: Use FormData to send File + Data together (Matching Lead's Screenshot)
      const formDataPayload = new FormData();
      formDataPayload.append('template_id', templateDetails.id);
      if (selectedLanguage?.id) {
        formDataPayload.append('lang_id', selectedLanguage.id);
      }
      formDataPayload.append('type_ext', selectedFileType.toUpperCase());

      const finalAgentIds = extraAgentIds
        ? [...new Set([...selectedAgentIds, ...extraAgentIds])]
        : [...selectedAgentIds];
      formDataPayload.append('agent_ids', JSON.stringify(finalAgentIds));

      // Send selected abbreviations as a JSON string
      // const selectedAbbrList = abbreviations?.filter(a => selectedAbbrevIds.includes(a.id)) || [];
      const selectedAbbrList =
        abbreviations
          ?.filter(a => selectedAbbrevIds.includes(a.id))
          .map(({ short_form, ...rest }) => ({
            ...rest,
            abbreviation: short_form
          })) || [];

      formDataPayload.append('abbreviations', JSON.stringify(selectedAbbrList));

      // Send the file if it exists in state
      if (frontPageFile) {
        formDataPayload.append('front_page', frontPageFile);
      }

      // CHANGE: Remove 'Content-Type': 'application/json' for FormData
      const multipartHeaders = { ...headers };
      delete multipartHeaders['Content-Type'];

      const response = await fetch(`${API_BASE_URL}/generate-report`, {
        method: 'POST',
        headers: multipartHeaders,
        body: formDataPayload
      });
      const data = await response.json();

      if (data.status === 'success') {
        setSuccessMessage(data.message || 'Report generation started successfully');
        setIsSuccessPopupVisible(true);
      } else {
        const errDetail = data.error ? `${data.message}: ${data.error}` : (data.message || 'Failed to generate report');
        setErrorMessage(errDetail);
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setErrorMessage('Network error occurred while generating report');
      setIsErrorPopupVisible(true);
    } finally {
      setIsGenerating(false);
    }
  };
  const handleClose = (cdt = 'cancel') => {
    // Reset state
    setSelectedLanguage(null);
    setSelectedFileType('');
    setIsGenerating(false);
    onClose(cdt);
  };
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    handleClose('gen')
  };


  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
    handleClose('error');
  };
  if (!isOpen) return null;

  const closeAbbrevErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  /* --- NEW CODE TO ADD --- */
  const handleFrontPageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 1. Check for 3MB Limit (3 * 1024 * 1024 bytes)
      if (file.size > 3 * 1024 * 1024) {
        setErrorMessage('File size exceeds 3MB limit');
        setIsErrorPopupVisible(true);
        e.target.value = null; // Reset the input
        return;
      }

      setFrontPageFile(file);

    }
  };

  // ── Agent selector helpers ──────────────────────────────────────────────────
  const getSelectableAgentIdsInSection = (sectionKey) => {
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return [];
    const ids = [];
    sec.agentOrder.forEach(agKey => {
      const agent = sec.agentsDict[agKey];
      if (agent?.id) ids.push(agent.id);
    });
    (formData?.sectionOrder || []).forEach(childKey => {
      if (formData.sectionsDict[childKey]?.parent_section_id === sectionKey) {
        ids.push(...getSelectableAgentIdsInSection(childKey));
      }
    });
    return ids;
  };

  const toggleAgentId = (agentId) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const toggleSectionAgents = (sectionKey, section) => {
    const allIds = getSelectableAgentIdsInSection(sectionKey);
    const orderingId = section.section_ordering_id;
    if (allIds.length === 0) {
      setSelectedSectionKeys(prev => {
        const next = new Set(prev);
        if (next.has(orderingId)) next.delete(orderingId);
        else next.add(orderingId);
        return next;
      });
      return;
    }
    const allSelected = allIds.every(id => selectedAgentIds.has(id));
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      allIds.forEach(id => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const selectAllAgents = () => {
    const allIds = new Set();
    const allOrderingIds = new Set();
    formData?.sectionOrder?.forEach(secKey => {
      const sec = formData.sectionsDict[secKey];
      if (!sec) return;
      allOrderingIds.add(sec.section_ordering_id);
      sec.agentOrder.forEach(agKey => {
        const agent = sec.agentsDict[agKey];
        if (agent?.id) allIds.add(agent.id);
      });
    });
    setSelectedAgentIds(allIds);
    setSelectedSectionKeys(allOrderingIds);
  };

  const deselectAllAgents = () => {
    setSelectedAgentIds(new Set());
    setSelectedSectionKeys(new Set());
  };

  const toggleSectionExpand = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getChildSections = (parentKey) => {
    return (formData?.sectionOrder || []).filter(
      key => formData.sectionsDict[key]?.parent_section_id === parentKey
    );
  };

  const isSectionVisible = (sectionKey) => {
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return false;
    if (!agentSearch.trim()) return true;
    const q = agentSearch.toLowerCase();
    if ((sec.section_name || '').toLowerCase().includes(q)) return true;
    if (sec.agentOrder.some(agKey => (sec.agentsDict[agKey]?.agent_name || '').toLowerCase().includes(q))) return true;
    return getChildSections(sectionKey).some(childKey => isSectionVisible(childKey));
  };

  const renderSectionRow = (sectionKey, depth = 0) => {
    if (!isSectionVisible(sectionKey)) return null;
    const sec = formData?.sectionsDict?.[sectionKey];
    if (!sec) return null;
    const allAgentIds = getSelectableAgentIdsInSection(sectionKey);
    const orderingId = sec.section_ordering_id;
    const isChecked = allAgentIds.length > 0
      ? allAgentIds.every(id => selectedAgentIds.has(id))
      : selectedSectionKeys.has(orderingId);
    const isIndeterminate = allAgentIds.length > 0 && !isChecked && allAgentIds.some(id => selectedAgentIds.has(id));
    const isExpanded = !!expandedSections[sectionKey];
    const childSections = getChildSections(sectionKey);
    const hasContent = sec.agentOrder.length > 0 || childSections.length > 0;
    const q = agentSearch.toLowerCase().trim();
    const visibleAgents = sec.agentOrder.filter(agKey => {
      const agent = sec.agentsDict[agKey];
      if (!agent) return false;
      if (!q) return true;
      return (agent.agent_name || '').toLowerCase().includes(q);
    });
    return (
      <div key={sectionKey} className="filter-picker-section-block">
        <div className="filter-picker-section-header d-flex align-items-center gap-2 ev-card-7 ev-radius-xsmall p-2 mb-1"
          style={{ '--filter-picker-depth': depth }}
          onClick={() => hasContent && toggleSectionExpand(sectionKey)}
        >
          <button type="button" className="btn p-0 border-0 bg-transparent ev-xsmall-p-blue"
            onClick={e => { e.stopPropagation(); hasContent && toggleSectionExpand(sectionKey); }}
            disabled={!hasContent}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
          </button>
          {/* Section always included — show tick/dash indicator only, not interactive */}
          <span
            className={`report-section-indicator ${isChecked || isIndeterminate ? 'indicator-active' : ''} cursor-pointer`}
            title={isChecked ? "Deselect all agents in this section" : "Select all agents in this section"}
            onClick={e => { e.stopPropagation(); toggleSectionAgents(sectionKey, sec); }}
          >
            {isChecked ? (
              <i className="bi bi-check"></i>
              ) : isIndeterminate ? (
              <i className="bi bi-dash"></i>
            ) : (
              <i className="bi bi-dash"></i>
            )}
          </span>
          <span className="ev-xsmall-p-blue ev-bold-reg me-1">{orderingId}</span>
          <span className="ev-xsmall-p-blue flex-grow-1 text-truncate min-w-0">{sec.section_name || '(Unnamed)'}</span>
          <span className="ev-xsmall-p-dark">{allAgentIds.filter(id => selectedAgentIds.has(id)).length}/{allAgentIds.length}</span>
        </div>
        {isExpanded && visibleAgents.map(agKey => {
          const agent = sec.agentsDict[agKey];
          const agId = agent?.id;
          const isSel = agId ? selectedAgentIds.has(agId) : false;
          const isDisabled = !agId;
          return (
            <div key={agKey}
              className={`ev-card-8 filter-picker-agent-row d-flex align-items-center p-1 mb-1 ev-radius-xsmall ${!isDisabled ? 'cursor-pointer' : ''}`}
              style={{ '--filter-picker-depth': depth }}
              onClick={() => !isDisabled && toggleAgentId(agId)}
            >
                <span
                  className={`report-section-indicator me-1 ms-1 ${isSel ? 'indicator-active' : ''} ${isDisabled ? 'opacity-50' : 'cursor-pointer'}`}
                  onClick={e => { e.stopPropagation(); !isDisabled && toggleAgentId(agId); }}
                  title={
                    !agId
                      ? "Save Preset first to select this agent"
                      : ""
                  }
                >
                  {isSel ? <i className="bi bi-check"></i> : <i className="bi bi-dash"></i>}
                </span>
                <span className="ev-xsmall-p-dark me-1 text-nowrap">{orderingId}.{agent.agent_ordering_id}</span>
                <span className="ev-small-p-dark flex-grow-1 text-truncate min-w-0">{agent.agent_name || `Agent ${agent.agent_ordering_id}`}</span>
                <span className="ms-2"><StatusBadgeSm status={agent.status || 'PENDING'} /></span>
            </div>
          );
        })}
        {isExpanded && childSections.map(childKey => renderSectionRow(childKey, depth + 1))}
      </div>
    );
  };

  const rootSections = (formData?.sectionOrder || []).filter(
    key => !formData?.sectionsDict?.[key]?.parent_section_id
  );
  const totalAgents = (formData?.sectionOrder || []).reduce((acc, secKey) => {
    const sec = formData?.sectionsDict?.[secKey];
    if (!sec) return acc;
    sec.agentOrder.forEach(agKey => { if (sec.agentsDict[agKey]?.id) acc++; });
    return acc;
  }, 0);
  // ─────────────────────────────────────────────────────────────────────────

  const findMissingDependencies = () => {
    const missing = [];
    const visited = new Set();
    const queue = [...selectedAgentIds];
    while (queue.length > 0) {
      const currId = queue.shift();
      if (visited.has(currId)) continue;
      visited.add(currId);
      let currAgent = null; let currSectionName = '';
      (formData?.sectionOrder || []).forEach(secKey => {
        const sec = formData.sectionsDict[secKey];
        if (!sec) return;
        (sec.agentOrder || []).forEach(agKey => {
          const ag = sec.agentsDict[agKey];
          if (ag?.id === currId) { currAgent = ag; currSectionName = sec.section_name || `${sec.section_ordering_id}`; }
        });
      });
      if (!currAgent) continue;
      (currAgent.reference_agent_ids || []).forEach(refId => {
        if (!selectedAgentIds.has(refId) && !missing.find(m => m.agentId === refId)) {
          let refAgent = null; let refSectionName = ''; let refSecAutogen = null;
          (formData?.sectionOrder || []).forEach(secKey => {
            const sec = formData.sectionsDict[secKey];
            if (!sec) return;
            (sec.agentOrder || []).forEach(agKey => {
              const ag = sec.agentsDict[agKey];
              if (ag?.id === refId) {
                refAgent = ag;
                refSectionName = sec.section_name || `${sec.section_ordering_id}`;
                refSecAutogen = sec.autogen_after || null;
              }
            });
          });
          if (refAgent) {
            missing.push({ agentId: refId, agentName: refAgent.agent_name || `Agent ${refAgent.agent_ordering_id}`, sectionName: refSectionName, referencedBy: currAgent.agent_name || `Agent ${currAgent.agent_ordering_id}` });
            queue.push(refId);
          }
        }
      });
    }
    return missing;
  };

  const handleGenerateClick = () => {
    const missing = findMissingDependencies();
    if (missing.length > 0) {
      setMissingDependencies(missing);
      setSelectedDepIds(new Set(missing.map(d => d.agentId)));
      setShowDependencyWarning(true);
    } else {
      handleGenerate();
    }
  };

  const addDependencyAndGenerate = () => {
    setShowDependencyWarning(false);
    handleGenerate([...selectedDepIds]);
  };

  const openAbbrevModal = () => {
    setShowAbbrevModal(true);
  };

  const closeAbbrevModal = () => {
    setShowAbbrevModal(false);
    cancelEditAbbreviation();
    cancelAddAbbreviation();
    closeMismatchPanel();
    if (abbreviationsOnly && onClose) onClose();
  };

  // The primary switch is only ever persisted as a field of the add/edit handlers, so a
  // user who flips it on a read row and clicks the footer Save would lose the change.
  // Push each flipped row through /update-abbreviation, which already demotes the old
  // holder of that short form. In practice this is one promotion plus one demotion.
  const savePrimaryFlagChanges = async () => {
    const before = new Set(serverPrimaryIds);
    const after = new Set(primaryAbbrevIds);
    const changed = abbreviations.filter((a) => before.has(a.id) !== after.has(a.id));
    for (const abbr of changed) {
      try {
        await fetch(`${API_BASE_URL}/update-abbreviation`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            template_id: templateDetails.id,
            abbreviation_id: abbr.id,
            short_form: abbr.short_form,
            full_form: abbr.full_form,
            original_full_form: abbr.original_full_form || '',
            original_language: abbr.original_language || '',
            is_foreign_origin: !!abbr.is_foreign_origin,
            is_primary: after.has(abbr.id)
          })
        });
      } catch (error) {
        console.error('Error saving primary flag:', error);
      }
    }
  };

  const saveAbbreviationSelection = async () => {
    try {
      await savePrimaryFlagChanges();
      const response = await fetch(`${API_BASE_URL}/save-abbreviation-selection`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          selected_ids: selectedAbbrevIds
        })
      });
      const data = await response.json();
      if (data.status !== "success") {
        console.error("Failed to save abbreviation selection", data);
      }
    } catch (error) {
      console.error("Error saving abbreviation selection:", error);
    }
  };
  const toggleAbbrevSelection = (id) => {
  const abbr = abbreviations.find((a) => a.id === id);
  if (abbr) {
    const selMap = getSelMap();
    const key = _abbrKey(abbr);
    if (selMap.has(key)) selMap.delete(key);
    else selMap.set(key, _normShort(abbr.short_form));
  }
  setSelectedAbbrevIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };
  const togglePrimaryStatus = (id) => {
    setPrimaryAbbrevIds(prev => {
      if (prev.includes(id)) {
        // Turning OFF - just remove this ID
        return prev.filter(item => item !== id);
      } else {
        // Turning ON - keep it, backend will handle same short_form conflict
        return [...prev, id];
      }
    });
  };
  // Rows inserted in this popup session get a "New" badge. The list is sorted by full
  // form, so a variant lands nowhere near the row it is a variant of.
  const markAbbreviationsNew = (inserted) => {
    const ids = (inserted || []).map((row) => row.id).filter((id) => id != null);
    if (ids.length === 0) return;
    setNewAbbrevIds(prev => new Set([...prev, ...ids]));
  };

  // The backend rejects a full form already attached to a DIFFERENT short form via a
  // unique index that is not in its ON CONFLICT target, so it comes back as a generic
  // failure. The list is already in state — say which row is in the way.
  const explainAddFailure = (shortForm, fullForm, message) => {
    const clash = abbreviations.find(
      (a) => (a.full_form || '').trim().toUpperCase() === fullForm.toUpperCase() &&
        _normShort(a.short_form) !== _normShort(shortForm)
    );
    if (clash) {
      return `'${fullForm}' is already the full form of '${clash.short_form}' in this preset. ` +
        `One full form can belong to only one short form, so free it up or use a different wording.`;
    }
    return message || 'Failed to add abbreviation';
  };

  const cancelAddAbbreviation = () => {
    setIsAddingAbbrev(false);
    setNewShortForm('');
    setNewFullForm('');
    setNewIsPrimary(false);
    setNewOriginalFullForm('');
    setNewOriginalLanguage('');
    setNewIsForeignOrigin(false);
  };

  const handleAddAbbreviation = async () => {
    if (!newShortForm.trim() || !newFullForm.trim()) return;

    // No client-side duplicate check. One short form may legitimately carry several
    // full forms (one primary, the rest variants); only the exact pair is unique, and
    // the backend reports that in `skipped`.
    setIsSavingAbbrev(true);
    try {
      const response = await fetch(`${API_BASE_URL}/add-abbreviations`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviations: [{
            short_form: newShortForm.trim(),
            full_form: newFullForm.trim(),
            original_full_form: newOriginalFullForm.trim(),
            original_language: newOriginalLanguage.trim(),
            is_foreign_origin: newIsForeignOrigin,
            is_primary: newIsPrimary
          }]
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const selMap = getSelMap();
        abbreviations
          .filter((a) => deleteTargetIds.includes(a.id))
          .forEach((a) => selMap.delete(_abbrKey(a)));
        const skipped = data.data?.skipped || [];
        const inserted = data.data?.inserted || [];
        markAbbreviationsNew(inserted);

        if (skipped.length > 0 && inserted.length === 0) {
          setErrorMessage(
            `'${skipped[0].short_form} — ${skipped[0].full_form}' is already in this preset. ` +
            `A short form may have several full forms, but not the same pair twice.`
          );
          setIsErrorPopupVisible(true);
        } else {
          cancelAddAbbreviation();
        }
        await fetchAbbreviations();
      } else {
        setErrorMessage(explainAddFailure(newShortForm.trim(), newFullForm.trim(), data.message));
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error adding abbreviation:', error);
      setErrorMessage('Network error while adding abbreviation');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
    }
  };

  const startEditAbbreviation = (abbr) => {
    setEditingAbbrevId(abbr.id);
    setEditShortForm(abbr.short_form);
    setEditFullForm(abbr.full_form);
    setEditOriginalFullForm(abbr.original_full_form || '');
    setEditOriginalLanguage(abbr.original_language || '');
    setEditIsForeignOrigin(!!abbr.is_foreign_origin);
    // The primary switch writes straight to primaryAbbrevIds, which the read row also
    // reads. Snapshot it so Cancel can put it back, like the two text fields.
    setEditPrimarySnapshot({ id: abbr.id, wasPrimary: primaryAbbrevIds.includes(abbr.id) });
  };

  // Leave edit mode keeping whatever is in primaryAbbrevIds — for the save path, where
  // the flag has just been persisted.
  const clearEditAbbreviationFields = () => {
    setEditPrimarySnapshot(null);
    setEditingAbbrevId(null);
    setEditShortForm('');
    setEditFullForm('');
    setEditOriginalFullForm('');
    setEditOriginalLanguage('');
    setEditIsForeignOrigin(false);
  };

  const cancelEditAbbreviation = () => {
    if (editPrimarySnapshot) {
      const { id, wasPrimary } = editPrimarySnapshot;
      setPrimaryAbbrevIds(prev => {
        if (prev.includes(id) === wasPrimary) return prev;
        return wasPrimary ? [...prev, id] : prev.filter(item => item !== id);
      });
    }
    clearEditAbbreviationFields();
  };

  const handleUpdateAbbreviation = async () => {
    if (!editShortForm.trim() || !editFullForm.trim()) return;
    setIsSavingAbbrev(true);
    try {
      const response = await fetch(`${API_BASE_URL}/update-abbreviation`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviation_id: editingAbbrevId,
          short_form: editShortForm.trim(),
          full_form: editFullForm.trim(),
          original_full_form: editOriginalFullForm.trim(),
          original_language: editOriginalLanguage.trim(),
          is_foreign_origin: editIsForeignOrigin,
          is_primary: primaryAbbrevIds.includes(editingAbbrevId)
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        clearEditAbbreviationFields();
        await fetchAbbreviations();
      } else {
        setErrorMessage(data.message || 'Failed to update abbreviation');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error updating abbreviation:', error);
      setErrorMessage('Network error while updating abbreviation');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
    }
  };

  const promptDeleteAbbreviations = (ids) => {
    setDeleteTargetIds(ids);
    setShowDeleteAbbrevConfirm(true);
  };

  const executeDeleteAbbreviations = async () => {
    setShowDeleteAbbrevConfirm(false);
    setIsSavingAbbrev(true);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/delete-abbreviations`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          template_id: templateDetails.id,
          abbreviation_ids: deleteTargetIds
        })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const selMap = getSelMap();
        abbreviations
          .filter((a) => deleteTargetIds.includes(a.id))
          .forEach((a) => selMap.delete(_abbrKey(a)));
        setSelectedAbbrevIds(prev => prev.filter(id => !deleteTargetIds.includes(id)));
        setDeleteTargetIds([]);
        await fetchAbbreviations();
      } else {
        setErrorMessage(data.message || 'Failed to delete abbreviation(s)');
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error deleting abbreviation(s):', error);
      setErrorMessage('Network error while deleting abbreviation(s)');
      setIsErrorPopupVisible(true);
    } finally {
      setIsSavingAbbrev(false);
      setIsLoading(false);
    }
  };

  // ── Mismatch review ─────────────────────────────────────────────────────────
  /**
   * Scan the preset's agent responses for spellings that resemble a registered
   * abbreviation but are not one of them, and open the review panel on the result.
   *
   * The endpoint streams newline-delimited JSON — a heartbeat every 5s, the envelope
   * last — because the scan reads one S3 object per agent. axios would buffer the
   * whole body and read `undefined`, so this goes through postNdjson.
   */
  const runMismatchDetection = async () => {
    setIsDetecting(true);
    setShowMismatchPanel(true);
    setMismatchNotice('');
    setMismatchCandidates([]);
    setPrimaryChoice({});
    setPickedKeys(new Set());
    setRejectedKeys(new Set());
    setExpandedExcerpts(new Set());
    cancelAddAbbreviation();
    cancelEditAbbreviation();
    try {
      const result = await postNdjson(
        `${API_BASE_URL}/detect-abbreviation-mismatches`,
        { template_id: templateDetails.id },
        headers,
      );
      const data = result.data;
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      if (data.status !== 'success') {
        setShowMismatchPanel(false);
        setErrorMessage(data.message || 'Failed to check spellings');
        setIsErrorPopupVisible(true);
        return;
      }
      const candidates = data.data?.candidates || [];
      setMismatchCandidates(candidates);
      // "No abbreviations registered" is not the same answer as "nothing wrong", and an
      // empty panel cannot tell them apart. Carry the backend's own wording through.
      setMismatchNotice(candidates.length === 0 ? (data.message || '') : '');
    } catch (error) {
      console.error('Error checking spellings:', error);
      setShowMismatchPanel(false);
      setErrorMessage('Network error while checking spellings');
      setIsErrorPopupVisible(true);
    } finally {
      setIsDetecting(false);
    }
  };

  const closeMismatchPanel = () => {
    setShowMismatchPanel(false);
    setMismatchCandidates([]);
    setMismatchNotice('');
    setPrimaryChoice({});
    setPickedKeys(new Set());
    setRejectedKeys(new Set());
    setExpandedExcerpts(new Set());
  };

  const toggleKeyIn = (setter, key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // Rejecting saves nothing. The row stays on screen, greyed and unticked, because a
  // reviewer who changes their mind has nothing to go back to once it is gone.
  const toggleRejected = (key) => {
    const undoing = rejectedKeys.has(key);
    toggleKeyIn(setRejectedKeys, key);
    // A rejected row cannot also be queued for saving.
    if (!undoing) setPickedKeys(prev => {
      if (!prev.has(key)) return prev;
      const kept = new Set(prev);
      kept.delete(key);
      return kept;
    });
  };

  /**
   * Save every ticked candidate in one go, which means two different things by kind.
   *
   * input: nothing; reads pickedKeys, mismatchCandidates and primaryChoice
   * output: nothing; the abbreviation list is refetched when anything saved
   * process:
   *     1. Split the ticked rows by kind. A "full_form" candidate is a NEW row under
   *        the same short form; an "original_full_form" candidate is a COLUMN on a row
   *        that already exists, so adding it as a full form would invent an
   *        abbreviation the replacer would then start expanding in the text
   *     2. Send every full_form candidate in ONE /add-abbreviations call — the backend
   *        loops the list, so twenty accepts are one request
   *     3. Send one /update-abbreviation per original_full_form candidate; there is no
   *        batch endpoint for a per-row column, and there are rarely many
   *     4. Drop what saved, keep what failed on screen with its reason, and refetch so
   *        the list and the New badges agree
   */
  const acceptPicked = async () => {
    const picked = mismatchCandidates.filter(c => pickedKeys.has(_candKey(c)));
    if (picked.length === 0) return;
    const newRows = picked.filter(c => c.kind !== 'original_full_form');
    const originEdits = picked.filter(c => c.kind === 'original_full_form');

    setIsAcceptingPicked(true);
    const savedKeys = new Set();
    const failures = [];
    try {
      if (newRows.length > 0) {
        const response = await fetch(`${API_BASE_URL}/add-abbreviations`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            template_id: templateDetails.id,
            abbreviations: newRows.map(c => ({
              short_form: c.matches_short_form,
              full_form: c.found,
              original_full_form: '',
              original_language: '',
              is_foreign_origin: false,
              is_primary: primaryChoice[_candKey(c)] === 'found'
            }))
          })
        });
        const data = await response.json();
        if (data.status === 'NotAuthorized') { authNavigate('/login', 'iam', navigate); return; }
        if (data.status === 'success') {
          markAbbreviationsNew(data.data?.inserted || []);
          // Only what the backend actually inserted leaves the panel. A skipped pair
          // stays visible with its reason rather than vanishing as if it had saved.
          const insertedForms = new Set((data.data?.inserted || []).map(r => (r.full_form || '').toUpperCase()));
          newRows.forEach(c => {
            if (insertedForms.has(c.found.toUpperCase())) savedKeys.add(_candKey(c));
            else failures.push(`'${c.matches_short_form} — ${c.found}' is already in this preset.`);
          });
        } else {
          failures.push(explainAddFailure(newRows[0].matches_short_form, newRows[0].found, data.message));
        }
      }

      // An origin edit is TWO columns, so only those two are sent. /update-abbreviation
      // leaves out what it is not given, so a row someone else changed since this panel
      // loaded keeps their edit, and a row that is not in the loaded list at all no
      // longer fails with "full_form cannot be empty".
      for (const candidate of originEdits) {
        const response = await fetch(`${API_BASE_URL}/update-abbreviation`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            template_id: templateDetails.id,
            abbreviation_id: candidate.matches_abbreviation_id,
            original_full_form: candidate.found,
            is_foreign_origin: true
          })
        });
        const data = await response.json();
        if (data.status === 'NotAuthorized') { authNavigate('/login', 'iam', navigate); return; }
        if (data.status === 'success') savedKeys.add(_candKey(candidate));
        else failures.push(data.message || `Could not record the original term '${candidate.found}'.`);
      }

      // "(current)" means the registered spelling is the one that prints. Nothing used to
      // act on that: the new row was inserted with is_primary false and the registered row
      // was left alone, so a short form with two rows ended up with no primary at all and
      // the renderer fell back to whichever row came back first. Promote it here, and only
      // when it is not already the primary, so this stays one call per real change.
      // newRows only: the radio is not rendered for an "Original term" candidate, so
      // its default there is a value nobody chose, not an answer.
      for (const candidate of newRows) {
        const key = _candKey(candidate);
        const registeredId = candidate.matches_abbreviation_id;
        const wantsRegistered = (primaryChoice[key] || 'registered') === 'registered';
        if (!savedKeys.has(key) || !wantsRegistered || !registeredId) continue;
        if (primaryAbbrevIds.includes(registeredId)) continue;
        const response = await fetch(`${API_BASE_URL}/update-abbreviation`, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            template_id: templateDetails.id,
            abbreviation_id: registeredId,
            is_primary: true
          })
        });
        const data = await response.json();
        if (data.status === 'NotAuthorized') { authNavigate('/login', 'iam', navigate); return; }
        if (data.status !== 'success') {
          failures.push(`Saved '${candidate.found}', but could not mark the existing `
            + `'${candidate.matches_short_form}' spelling as the primary one.`);
        }
      }

      if (savedKeys.size > 0) {
        setMismatchCandidates(prev => prev.filter(c => !savedKeys.has(_candKey(c))));
        setPickedKeys(prev => new Set([...prev].filter(k => !savedKeys.has(k))));
        await fetchAbbreviations();
      }
      if (failures.length > 0) {
        setErrorMessage(failures.join(' '));
        setIsErrorPopupVisible(true);
      }
    } catch (error) {
      console.error('Error saving mismatch candidates:', error);
      setErrorMessage('Network error while saving the spellings');
      setIsErrorPopupVisible(true);
    } finally {
      setIsAcceptingPicked(false);
    }
  };

  // Both spellings are rendered through this, so the words that differ are marked on
  // whichever one is being shown.
  const renderDiff = (text, against) => {
    const { head, mid, tail } = _diffSplit(text || '', against || '');
    return (
      <>
        {head}
        {mid && <span className="abbrmatch-diff">{mid}</span>}
        {tail}
      </>
    );
  };

  return (
    <>
      {!abbreviationsOnly && ReactDOM.createPortal(
        <div className="modal fade show d-block bg-dark bg-opacity-50" style={{ zIndex: 10000 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered w-40">
          <div className="modal-content ev-message-card2 d-block ev-card-2 ev-padding-large">

            {/* Portal Tooltip - renders outside modal, no clipping */}
            {tooltipVisible && templateDetails && ReactDOM.createPortal(
              <div
                className="avatar-tooltip-portal"
                style={{ top: tooltipPos.top, left: tooltipPos.left }}
              >
                {templateDetails.name}
              </div>,
              document.body
            )}

            {/* --- UPDATED HEADER WITH ABBREVIATION BUTTON --- */}
            <div className="mb-4 d-flex justify-content-between align-items-center gap-2">
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                <span className="ev-med-p-blue flex-shrink-0">Generate Word Report</span>
                {templateDetails && (
                  <div
                    ref={badgeRef}
                    className="ev-message-card3 text-truncate"
                    style={{ minWidth: 0 }}
                    onMouseEnter={() => {
                      const r = badgeRef.current?.getBoundingClientRect();
                      if (r) setTooltipPos({ top: r.top - 4, left: r.left + r.width / 2 });
                      setTooltipVisible(true);
                    }}
                    onMouseLeave={() => setTooltipVisible(false)}
                  >
                    <i className="bi bi-kanban me-1"></i> Preset: <span className="ev-bold-med">{templateDetails.name}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="ev-btn-blue-outline-round"
                onClick={openAbbrevModal}
              >
                Select Abbreviations
              </button>
            </div>

            {/* Modal Body */}
            <div>

              {hasPendingChanges && (
                <div className="ev-alert-card-warning mb-3 ev-btn-small d-flex align-items-center gap-2">
                  <i className="bi bi-exclamation-triangle"></i>
                  <span>You have unsaved changes. They will be saved before generating. To discard, click Refresh first.</span>
                </div>
              )}

              {/* --- AGENT SELECTION SECTION --- */}
              {formData?.sectionOrder?.length > 0 && (
                <div className="mb-4">
                  <div className="ev-card-8 p-3 report-section-card">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="ev-small-p-blue ev-bold-reg">
                          <i className="bi bi-cpu me-1"></i>Select Agents for Report
                        </span>
                        <span className="ev-message-card1 ev-btn-xsmall">{selectedAgentIds.size}/{totalAgents}</span>
                      </div>
                      <div className="d-flex gap-1">
                        <button type="button"
                          className="ev-btn-light-fill ev-btn-xsmall ev-bold-reg border avatar-tooltip"
                          data-tooltip={rootSections.every(k => expandedSections[k]) ? 'Collapse all sections' : 'Expand all sections'}
                          onClick={() => {
                            const allExpanded = rootSections.every(k => expandedSections[k]);
                            const next = {};
                            (formData?.sectionOrder || []).forEach(k => { next[k] = !allExpanded; });
                            setExpandedSections(next);
                          }}
                        >
                          <i className={`bi ${rootSections.every(k => expandedSections[k]) ? 'bi-arrows-collapse' : 'bi-arrows-expand'} me-1`}></i>
                          {rootSections.every(k => expandedSections[k]) ? 'Collapse All' : 'Expand All'}
                        </button>
                        <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg" onClick={selectAllAgents}>Select All</button>
                        <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg" onClick={deselectAllAgents}>Deselect All</button>
                      </div>
                    </div>
                    <div className="position-relative mb-2">
                      <input type="text" className="form-control search-input ms-0" placeholder="Search agents..."
                        value={agentSearch} onChange={e => setAgentSearch(e.target.value)}
                      />
                      <button className="ev-btn-lightblue-fill-round search-bar-button">
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    {selectedAgentIds.size === 0 && (
                      <div className="ev-alert-card-warning mb-2 ev-btn-small">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Select at least one agent to include in the report.
                      </div>
                    )}
                    <div className="report-agent-list-container scrollable-element d-flex flex-column gap-1">
                      <div className="d-flex flex-column gap-1 pe-2">
                        {rootSections.map(key => renderSectionRow(key, 0))}
                        {rootSections.every(key => !isSectionVisible(key)) && (
                          <div className="text-center p-3">
                            <i className="bi bi-search ev-fs-25 ev-small-p-blue"></i>
                            <div className="ev-small-p-blue mt-2">No agents match your search</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Language Selection */}
              {/* <div className="mb-4">
                <LanguageSelector
                  options={languages}
                  isLoading={isLoadingLanguages}
                  selectedLanguage={selectedLanguage}
                  onLanguageSelect={handleLanguageSelect}
                />
                <div className="text-end mt-2">
                  <span className="ev-alert-card-warning ev-btn-small ev-radius-xsmall">
                    Leave empty to use template's default language
                  </span>
                </div>
              </div> */}
            </div>
            {/* --- ADD FRONT PAGE SECTION ---
            <div className="mb-4">
              <div className="ev-card-8 p-3 d-flex justify-content-between align-items-center" style={{ borderStyle: 'dashed' }}>
                <div>
                  <div className="ev-small-p-blue ev-bold-reg">Add Front Page</div>
                  <div className="ev-xsmall-p-light fst-italic">
                    (Headers & footers will be applied to all pages)
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <div>
                    <input
                      type="file"
                      className="choose-files"
                      accept=".docx,.pdf,.pptx"
                      onChange={handleFrontPageUpload}

                    />
                  </div>
                </div>
              </div>
            </div>
            --- */}
            {/* Modal Footer */}
            <div className="d-flex justify-content-end align-items-center gap-2 pt-3">
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-bold-reg"
                onClick={() => handleClose('cancel')}
                disabled={isGenerating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg"
                onClick={handleGenerateClick}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" style={{borderColor: 'white', borderRightColor: 'transparent'}} role="status" aria-hidden="true"></span>
                    Generating
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </div>
        </div>
        </div>,
        document.body
      )}

      {showAbbrevModal && ReactDOM.createPortal(
        <div className="popup" style={{ zIndex: 10000 }}>
          <div className="popup-content ev-message-card2 d-block ev-card-2 w-40">

            {/* Modal Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="ev-med-p-blue mb-0">
                {showMismatchPanel ? 'Check Spellings in Text' : 'Abbreviations'}
              </h5>
              <div className="d-flex align-items-center gap-1">
                {!showMismatchPanel && canHere(PERMISSIONS.ABBREV_DETECT) && (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
                    onClick={runMismatchDetection}
                    disabled={isDetecting || isSavingAbbrev}
                  >
                    {isDetecting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Checking
                      </>
                    ) : (
                      <><i className="bi bi-search me-1"></i> Check Spellings in Text</>
                    )}
                  </button>
                )}
                {!showMismatchPanel && canHere(PERMISSIONS.ABBREV_ADD) && (
                  <button
                    className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => { setIsAddingAbbrev(true); cancelEditAbbreviation(); }}
                    disabled={isAddingAbbrev || isSavingAbbrev}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Add
                  </button>
                )}
                {!showMismatchPanel && canHere(PERMISSIONS.ABBREV_DELETE) && selectedAbbrevIds.length > 0 && (
                  <button
                    className="ev-btn-red-fill-round ev-btn-small ev-bold-reg"
                    onClick={() => promptDeleteAbbreviations(selectedAbbrevIds)}
                    disabled={isSavingAbbrev}
                  >
                    <i className="bi bi-trash me-1"></i> Delete ({selectedAbbrevIds.length})
                  </button>
                )}
                {showMismatchPanel && (
                  <button
                    className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
                    onClick={closeMismatchPanel}
                    disabled={isDetecting || isAcceptingPicked}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back to Abbreviations
                  </button>
                )}
              </div>
            </div>

            {showMismatchPanel ? (
              /* --- MISMATCH REVIEW PANEL --- */
              <>
                {isDetecting && (
                  <div className="text-center p-4">
                    <span className="spinner-border ev-small-p-blue" role="status" aria-hidden="true"></span>
                    <div className="ev-small-p-blue mt-2">Reading the report text. This can take a minute.</div>
                  </div>
                )}

                {!isDetecting && mismatchCandidates.length === 0 && (
                  <div className="text-center p-4">
                    <i className="bi bi-check2-circle ev-fs-25 ev-small-p-blue"></i>
                    <div className="ev-small-p-blue mt-2">
                      {mismatchNotice || 'Nothing left to review. Every spelling in the text matches a registered abbreviation.'}
                    </div>
                  </div>
                )}

                {!isDetecting && mismatchCandidates.length > 0 && (
                  <>
                    {/* Action bar — one save for everything ticked */}
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                      <button
                        className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
                        onClick={acceptPicked}
                        disabled={pickedKeys.size === 0 || isAcceptingPicked}
                      >
                        {isAcceptingPicked ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Saving
                          </>
                        ) : (
                          <><i className="bi bi-check-lg me-1"></i> Accept Selected ({pickedKeys.size})</>
                        )}
                      </button>
                      <button
                        className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                        disabled={isAcceptingPicked}
                        onClick={() => setPickedKeys(new Set(
                          mismatchCandidates.map(_candKey).filter(k => !rejectedKeys.has(k))
                        ))}
                      >
                        Select All
                      </button>
                      <button
                        className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                        disabled={isAcceptingPicked}
                        onClick={() => setPickedKeys(new Set())}
                      >
                        Clear
                      </button>
                      <span className="ev-xsmall-p-dark ms-auto">
                        {mismatchCandidates.length} to review
                        {rejectedKeys.size > 0 && <> · {rejectedKeys.size} rejected</>}
                      </span>
                    </div>

                    <div className="abbrmatch-list">
                      <div className="d-flex flex-column gap-1 pe-2">
                        {mismatchCandidates.map((candidate) => {
                          const key = _candKey(candidate);
                          const hint = _diffHint(candidate.found, candidate.matches_spelling);
                          const isOriginal = candidate.kind === 'original_full_form';
                          const isRejected = rejectedKeys.has(key);
                          const isPicked = pickedKeys.has(key);
                          const choice = primaryChoice[key] || 'registered';
                          return (
                            <div
                              key={key}
                              className={`abbrmatch-row p-2 ev-radius-small ${isRejected ? 'abbrmatch-row-rejected' : isPicked ? 'ev-card-1' : 'ev-card-2'}`}
                            >
                              {/* Line 1 — what it is, where it is, what to do with it */}
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <input
                                  type="checkbox"
                                  className="form-check-input m-0 shadow-sm"
                                  checked={isPicked}
                                  disabled={isRejected || isAcceptingPicked}
                                  onChange={() => toggleKeyIn(setPickedKeys, key)}
                                />
                                <span className="ev-message-card1">{candidate.matches_short_form}</span>
                                <span className={`ev-btn-xsmall ev-fs-11 text-nowrap ${isOriginal ? 'ev-message-card3' : 'ev-message-card3-green'}`}>
                                  {isOriginal ? 'Original term' : 'Full form'}
                                </span>
                                {hint && (
                                  <span className="ev-alert-card-warning ev-btn-xsmall ev-fs-11 ev-radius-xsmall text-nowrap">
                                    {hint}
                                  </span>
                                )}
                                <span className="ev-xsmall-p-dark text-truncate abbrmatch-where">
                                  {candidate.occurrences}× · {(candidate.sections || []).join(', ')}
                                </span>
                                <div className="d-flex gap-1 ms-auto">
                                  <button
                                    className="ev-btn-light-fill ev-btn-xsmall ev-bold-reg border"
                                    onClick={() => toggleKeyIn(setExpandedExcerpts, key)}
                                  >
                                    <i className={`bi ${expandedExcerpts.has(key) ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
                                    Text
                                  </button>
                                  <button
                                    className={`ev-btn-xsmall ev-bold-reg ${isRejected ? 'ev-btn-light-fill border' : 'ev-btn-blue-outline-round'}`}
                                    disabled={isAcceptingPicked}
                                    onClick={() => toggleRejected(key)}
                                  >
                                    <i className={`bi ${isRejected ? 'bi-arrow-counterclockwise' : 'bi-x-lg'} me-1`}></i>
                                    {isRejected ? 'Undo' : 'Reject'}
                                  </button>
                                </div>
                              </div>

                              {/* Line 2 — the two spellings ARE the choice of which one prints */}
                              {!isOriginal ? (
                                <div className="d-flex align-items-center gap-2 flex-wrap mt-1 abbrmatch-choice">
                                  <span className="ev-xsmall-p-dark">Print:</span>
                                  {[
                                    { value: 'registered', text: candidate.matches_spelling, other: candidate.found },
                                    { value: 'found', text: candidate.found, other: candidate.matches_spelling },
                                  ].map(option => (
                                    <label
                                      key={option.value}
                                      htmlFor={`primary-${key}-${option.value}`}
                                      className={`abbrmatch-pick ${choice === option.value ? 'abbrmatch-pick-on' : ''}`}
                                    >
                                      <input
                                        className="form-check-input m-0"
                                        type="radio"
                                        name={`primary-${key}`}
                                        id={`primary-${key}-${option.value}`}
                                        checked={choice === option.value}
                                        disabled={isRejected || isAcceptingPicked}
                                        onChange={() => setPrimaryChoice(prev => ({ ...prev, [key]: option.value }))}
                                      />
                                      <span className="abbrmatch-mono">{renderDiff(option.text, option.other)}</span>
                                      {option.value === 'registered' && <span className="ev-fs-11">(current)</span>}
                                      {option.value === 'found' && <span className="ev-fs-11">(in text)</span>}
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <div className="d-flex align-items-center gap-2 flex-wrap mt-1 abbrmatch-choice">
                                  <span className="ev-xsmall-p-dark">Record as original:</span>
                                  <span className="abbrmatch-mono ev-small-p-dark text-decoration-line-through opacity-75">
                                    {candidate.matches_spelling}
                                  </span>
                                  <i className="bi bi-arrow-right ev-xsmall-p-dark"></i>
                                  <span className="abbrmatch-mono ev-small-p-dark">
                                    {renderDiff(candidate.found, candidate.matches_spelling)}
                                  </span>
                                </div>
                              )}

                              {expandedExcerpts.has(key) && (
                                <div className="ev-card-8 ev-radius-xsmall p-2 mt-1 ev-xsmall-p-dark fst-italic">
                                  …{candidate.excerpt}…
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
            <>
            {/* Select / Deselect All */}
            <div className="d-flex gap-1 mb-2">
              <button className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg" onClick={() => { const m = getSelMap(); m.clear(); abbreviations.forEach(a => m.set(_abbrKey(a), _normShort(a.short_form))); setSelectedAbbrevIds(abbreviations.map(a => a.id)); }}>Select All</button>
              <button className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg" onClick={() => { getSelMap().clear(); setSelectedAbbrevIds([]); }}>Deselect All</button>
            </div>

            {/* Search Bar */}
            <input
              type="text"
              className="search-input mb-3"
              placeholder="Search Abbreviations..."
              value={abbrevSearch}
              onChange={(e) => setAbbrevSearch(e.target.value)}
            />

            {/* Inline Add Form */}
            {isAddingAbbrev && (
              <div className="ev-card-8 ev-radius-small p-2 mb-3">
                <div className="ev-small-p-blue ev-bold-med mb-2">
                  <i className="bi bi-plus-circle me-1"></i>New Abbreviation
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Short Form (e.g. RCT)"
                    value={newShortForm}
                    onChange={(e) => setNewShortForm(e.target.value)}
                    style={{ flex: '1' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Full Form (e.g. Randomized Controlled Trial)"
                    value={newFullForm}
                    onChange={(e) => setNewFullForm(e.target.value)}
                    style={{ flex: '2' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <div className="text-center d-flex justify-content-center w-15">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={newIsPrimary}
                        onChange={(e) => setNewIsPrimary(e.target.checked)}
                        id="primary-add-new"
                      />
                    </div>
                  </div>
                  <button
                    className="ev-btn-blue-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                    onClick={handleAddAbbreviation}
                    disabled={!newShortForm.trim() || !newFullForm.trim() || isSavingAbbrev}
                  >
                    {isSavingAbbrev ? <span className="spinner-border spinner-border-sm text-white" role="status"></span> : <i className="bi bi-check-lg"></i>}
                  </button>
                  <button
                    className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                    onClick={cancelAddAbbreviation}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <div className="d-flex gap-2 align-items-center mt-2">
                  <span className="ev-small-p-blue ev-fs-12" style={{ whiteSpace: 'nowrap' }}>Origin</span>
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Original Full Form (e.g. European Medicines Agency)"
                    value={newOriginalFullForm}
                    onChange={(e) => setNewOriginalFullForm(e.target.value)}
                    style={{ flex: '2' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <input
                    type="text"
                    className="formField2 ev-btn-small ev-fs-12"
                    placeholder="Original Language (e.g. English)"
                    value={newOriginalLanguage}
                    onChange={(e) => setNewOriginalLanguage(e.target.value)}
                    style={{ flex: '1' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddAbbreviation(); }}
                  />
                  <div className="form-check form-switch d-flex align-items-center gap-2 m-0">
                    <input
                      className="form-check-input m-0"
                      type="checkbox"
                      role="switch"
                      checked={newIsForeignOrigin}
                      onChange={(e) => setNewIsForeignOrigin(e.target.checked)}
                      id="foreign-origin-add-new"
                    />
                    <label className="ev-small-p-blue ev-fs-12 m-0" htmlFor="foreign-origin-add-new" style={{ whiteSpace: 'nowrap' }}>
                      Foreign origin
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* List Header */}
            <div className="d-flex justify-content-between px-2 mb-2">
              <span
                className="ev-small-p-blue ev-bold-reg"
                style={{ flex: "2", paddingLeft: "28px" }}
              >
                Full Form
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ flex: "1" }}
              >
                Abbreviation
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ flex: "1.4" }}
              >
                Original Term
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ flex: "0.8" }}
              >
                Language
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ width: "90px" }}
              >
                Is Primary
              </span>
              <span
                className="ev-small-p-blue ev-bold-reg text-center"
                style={{ width: "70px" }}
              >
                Actions
              </span>
            </div>

            {/* Scrollable List */}
            <div className="table-fixed-height-scrollable flex-grow-1 overflow-auto">
              <div className="d-flex flex-column gap-2">
                {abbreviations
                  .filter(abbr =>
                    abbr.full_form.toLowerCase().includes(abbrevSearch.toLowerCase()) ||
                    abbr.short_form.toLowerCase().includes(abbrevSearch.toLowerCase())
                  )
                  .map((abbr, index) => (
                    <div
                      key={abbr.id}
                      className={`d-flex ${editingAbbrevId === abbr.id ? 'flex-column gap-2' : 'justify-content-between align-items-center'} p-2 ev-radius-small ${selectedAbbrevIds.includes(abbr.id) ? 'ev-card-1' : index % 2 === 0 ? 'ev-card-2' : 'ev-card-3'
                        }`}
                    >

                      {editingAbbrevId === abbr.id ? (
                        <>
                          <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2" style={{ flex: '2' }}>
                            <input type="checkbox" className="form-check-input" checked={selectedAbbrevIds.includes(abbr.id)} onChange={() => toggleAbbrevSelection(abbr.id)} />
                            <input type="text" className="formField2 ev-btn-small ev-fs-12" value={editFullForm} onChange={(e) => setEditFullForm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                          </div>
                          <div className="text-center" style={{ flex: '1' }}>
                            <input type="text" className="ms-1 formField2 ev-btn-small ev-fs-12 text-center" value={editShortForm} onChange={(e) => setEditShortForm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                          </div>
                          <div className="text-center d-flex justify-content-center w-15">
                            <div className="form-check form-switch">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                role="switch"
                                checked={primaryAbbrevIds.includes(abbr.id)} 
                                onChange={() => togglePrimaryStatus(abbr.id)}
                                id={`primary-edit-${abbr.id}`}
                              />
                            </div>
                          </div>
                          <div className="d-flex gap-1 justify-content-center" style={{ width: '70px' }}>
                            <button className="ev-btn-blue-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={handleUpdateAbbreviation} disabled={!editShortForm.trim() || !editFullForm.trim() || isSavingAbbrev}>
                              {isSavingAbbrev ? <span className="spinner-border spinner-border-sm text-white" role="status"></span> : <i className="bi bi-check-lg"></i>}
                            </button>
                            <button className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={cancelEditAbbreviation}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                          </div>
                          <div className="d-flex gap-2 align-items-center">
                            <span className="ev-small-p-blue ev-fs-12" style={{ whiteSpace: 'nowrap' }}>Origin</span>
                            <input type="text" className="formField2 ev-btn-small ev-fs-12" style={{ flex: '2' }}
                              placeholder="Original Full Form (e.g. European Medicines Agency)"
                              value={editOriginalFullForm} onChange={(e) => setEditOriginalFullForm(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                            <input type="text" className="formField2 ev-btn-small ev-fs-12" style={{ flex: '1' }}
                              placeholder="Original Language (e.g. English)"
                              value={editOriginalLanguage} onChange={(e) => setEditOriginalLanguage(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateAbbreviation(); if (e.key === 'Escape') cancelEditAbbreviation(); }} />
                            <div className="form-check form-switch d-flex align-items-center gap-2 m-0">
                              <input className="form-check-input m-0" type="checkbox" role="switch"
                                checked={editIsForeignOrigin} onChange={(e) => setEditIsForeignOrigin(e.target.checked)}
                                id={`foreign-origin-edit-${abbr.id}`} />
                              <label className="ev-small-p-blue ev-fs-12 m-0" htmlFor={`foreign-origin-edit-${abbr.id}`} style={{ whiteSpace: 'nowrap' }}>
                                Foreign origin
                              </label>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="d-flex align-items-center gap-2" style={{ flex: '2' }}>
                            <input type="checkbox" className="form-check-input shadow-sm" checked={selectedAbbrevIds.includes(abbr.id)} onChange={() => toggleAbbrevSelection(abbr.id)} />
                            <span className="ev-small-p-dark avatar-tooltip" data-tooltip={abbr.full_form || 'None'}><TrimContent text={abbr.full_form} maxLength={47} /></span>
                          </div>
                          <span className="text-center d-flex align-items-center justify-content-center gap-1" style={{ flex: '1' }}>
                            <span className={primaryAbbrevIds.includes(abbr.id) ? 'ev-message-card3-green' : 'ev-message-card1'}>
                              {abbr.short_form}
                            </span>
                            {newAbbrevIds.has(abbr.id) && (
                              <span className="abbrmatch-new-pill ev-btn-xsmall ev-fs-11 text-nowrap">New</span>
                            )}
                          </span>
                          {/* Origin is two facts, so it gets two columns. The term shows
                              whenever one is recorded, flag or no flag — a stored term the
                              user cannot see is a term they cannot correct. */}
                          <span className="text-center" style={{ flex: '1.4' }}>
                            {(abbr.original_full_form || '').trim() ? (
                              <span className="ev-small-p-dark avatar-tooltip" data-tooltip={abbr.original_full_form}>
                                <TrimContent text={abbr.original_full_form} maxLength={30} />
                              </span>
                            ) : abbr.is_foreign_origin ? (
                              <span className="ev-xsmall-p-dark fst-italic">not recorded</span>
                            ) : null}
                          </span>
                          <span className="text-center" style={{ flex: '0.8' }}>
                            {(abbr.original_language || '').trim() && (
                              <span className="ev-small-p-dark">
                                <TrimContent text={abbr.original_language} maxLength={14} />
                              </span>
                            )}
                          </span>
                          <div
                            className="text-center d-flex justify-content-center w-15"
                          >
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={primaryAbbrevIds.includes(abbr.id)} 
                                disabled 
                                id={`primary-edit-${abbr.id}`}
                              />
                            </div>
                          </div>
                          <div
                            className="d-flex gap-1 justify-content-center"
                            style={{ width: "70px" }}
                          >
                            {canHere(PERMISSIONS.ABBREV_UPDATE) && (
                              <button
                                className="ev-btn-blue-outline-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10"
                                onClick={() => startEditAbbreviation(abbr)}
                                disabled={isSavingAbbrev}
                              >
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                            )}
                            {canHere(PERMISSIONS.ABBREV_DELETE) && (
                              <button className="ev-btn-red-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10" onClick={() => promptDeleteAbbreviations([abbr.id])} disabled={isSavingAbbrev}>
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                {abbreviations.filter(abbr =>
                  abbr.full_form.toLowerCase().includes(abbrevSearch.toLowerCase()) ||
                  abbr.short_form.toLowerCase().includes(abbrevSearch.toLowerCase())
                ).length === 0 && (
                    <div className="text-center p-4">
                      <i className="bi bi-journal-x ev-fs-25 ev-small-p-blue"></i>
                      <div className="ev-small-p-blue mt-2">
                        {abbrevSearch ? 'No matching abbreviations found' : 'No abbreviations yet. Click Add to create one.'}
                      </div>
                    </div>
                  )}
              </div>
            </div>
            </>
            )}

            {/* Modal Footer */}
            <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
              <button className="ev-btn-blue-outline-round" onClick={closeAbbrevModal}>
                {canHere(PERMISSIONS.ABBREV_SAVE_SELECTION) ? 'Cancel' : 'Close'}
              </button>
              {canHere(PERMISSIONS.ABBREV_SAVE_SELECTION) && (
                <button className="ev-btn-blue-fill-round" onClick={() => { saveAbbreviationSelection(); closeAbbrevModal(); }}>Save</button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div style={{ position: 'relative', zIndex: 2100 }}>
        <DeleteConfirmationModal
          show={showDeleteAbbrevConfirm}
          onClose={() => { setShowDeleteAbbrevConfirm(false); setDeleteTargetIds([]); }}
          onConfirm={executeDeleteAbbreviations}
          objectName="abbreviation"
          itemName={`${deleteTargetIds.length} abbreviation${deleteTargetIds.length > 1 ? 's' : ''}`}
          bodyContent={
            <p className="text-danger mb-0">
              This action cannot be undone.
            </p>
          }
        />
      </div>

      {/* Dependency Warning Popup */}
      {showDependencyWarning && (
        <div className="popup">
          <div className="ev-card-4 p-4 report-dep-popup-card">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-exclamation-triangle-fill text-warning report-dep-warning-icon"></i>
                <span className="ev-reg-p-blue ev-bold-reg">Missing Agent Dependencies</span>
              </div>
              <button type="button" className="ev-btn-light-fill ev-btn-xsmall" onClick={() => setShowDependencyWarning(false)}>
                <i className="bi bi-x"></i>
              </button>
            </div>
            <p className="ev-xsmall-p-dark mb-3">
              The following agents are referenced by your selected agents but are not included in the report.
              Missing dependencies may cause broken cross-references. Select which ones to add.
            </p>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="ev-xsmall-p-blue ev-bold-reg">{selectedDepIds.size}/{missingDependencies.length} selected</span>
              <div className="d-flex gap-1">
                <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall ev-bold-reg"
                  onClick={() => setSelectedDepIds(new Set(missingDependencies.map(d => d.agentId)))}>
                  Select All
                </button>
                <button type="button" className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                  onClick={() => setSelectedDepIds(new Set())}>
                  Clear
                </button>
              </div>
            </div>
            <div className="report-dep-list mb-3">
              <div className="d-flex flex-column gap-1">
                {missingDependencies.map(dep => {
                  const isSel = selectedDepIds.has(dep.agentId);
                  return (
                    <div key={dep.agentId}
                      className={`d-flex align-items-center gap-2 p-2 ev-radius-xsmall cursor-pointer ${isSel ? 'ev-card-1' : 'ev-card-2'}`}
                      onClick={() => setSelectedDepIds(prev => { const next = new Set(prev); if (next.has(dep.agentId)) next.delete(dep.agentId); else next.add(dep.agentId); return next; })}
                    >
                      <input type="checkbox" className="form-check-input m-0 shadow-none cursor-pointer"
                        checked={isSel}
                        onChange={() => setSelectedDepIds(prev => { const next = new Set(prev); if (next.has(dep.agentId)) next.delete(dep.agentId); else next.add(dep.agentId); return next; })}
                        onClick={e => e.stopPropagation()}
                      />
                      <i className="bi bi-link-45deg ev-xsmall-p-blue report-dep-link-icon"></i>
                      <div className="report-dep-info">
                        <div className="ev-small-p-dark ev-bold-reg text-truncate">{dep.agentName}</div>
                        <div className="ev-xsmall-p-dark">{dep.sectionName} · referenced by <em>{dep.referencedBy}</em></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg"
                onClick={() => { setShowDependencyWarning(false); handleGenerate(); }}>
                Skip & Generate Anyway
              </button>
              <div className="d-flex gap-2">
                <button type="button" className="ev-btn-white-fill-round ev-bold-reg report-dep-cancel-btn"
                  onClick={() => setShowDependencyWarning(false)}>
                  Cancel
                </button>
                <button type="button" className="ev-btn-blue-fill-round ev-bold-reg"
                  disabled={selectedDepIds.size === 0}
                  onClick={addDependencyAndGenerate}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Selected & Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={successMessage}
        />

      )}
      {/* Error Popup */}
      {isErrorPopupVisible && (
        <ErrorPopup
          onClose={showAbbrevModal ? closeAbbrevErrorPopup : closeErrorPopup}
          message={errorMessage}
        />
      )}


      {isLoading && (
        <ProcessingPopup message="Processing, please wait..." />
      )}
    </>
  );
};

export default ReportModalForm;